import React, { useMemo } from 'react';
import {
  Row,
  Col,
} from "tea-component";
import { useIntl } from 'umi'
import { isGreater } from '@/pages/dashboard/pages/overview/_config/common'
import { formatNum, formatPercentNum, formatSmallValue } from '@/pages/dashboard/utils/utils'
import useDailyKeyMetricsModel from '@/pages/dashboard/servicesModels/overview/dailyKeyMetricsModel';
import useWeeklyKeyMetricsModel from '@/pages/dashboard/servicesModels/overview/weeklyKeyMetricsModel';
import useMonthlyKeyMetricsModel from '@/pages/dashboard/servicesModels/overview/monthlyKeyMetricsModel';
import useDailyKeyMetricsTrendModel from '@/pages/dashboard/servicesModels/overview/dailyKeyMetricsTrendModel';
import useWeeklyKeyMetricsTrendModel from '@/pages/dashboard/servicesModels/overview/weeklyKeyMetricsTrendModel';
import useMonthlyKeyMetricsTrendModel from '@/pages/dashboard/servicesModels/overview/monthlyKeyMetricsTrendModel';

import Abnormal from '@/pages/dashboard/components/Abnormal';
import CardContext, { MetricsItemInfo } from './CardContext'
import useOverviewLocalesIntl from '@/pages/dashboard/pages/overview/_config/useOverviewLocalesIntl'
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';

/**
 * @description: 卡片位指标数值超过百万，需要增加单位M|B
 * @param {string|number} data
 * @return {string}
 */
const unitConversionMillion = (data): string => {
  if (data >= 1000000 && data < 1000000000) {
    return (Math.round(data / 10000) / 100).toFixed(2) + 'M'
  }
  return (Math.round(data / 10000000) / 100).toFixed(2) + 'B'
}
/**
 * 卡片主数据
 */
export interface MetricsItem {
  metricsName: string // 卡片名称
  value: string // 卡片主数据值
  dod: string // 同比(时间范围=1天)
  sdlw: string // 环比(时间范围=1天)
  type: string
  title: string
  time: string
  dodTime: string
  sdlwTime: string
  dodCount: string
  sdlwCount: string
  cardHourTime: string
}

/**
 * Props传参
 */
interface Props {
  isShowArrow: boolean // 跳转箭头
  dataSource: MetricsItem // 卡片数据
}

/**
 * 变化率
 */
interface PercentType {
  title: string // 变化率标题
  value: string // 变化率数据
}

export default function KeyPerformanceMetrics(props: any) {
  const {
    parentProps,
    formValue,
    gameCode,
    dispatch,
    filterLoading,
    dateDimension,
    // isSmall=false,
    dailyReqType,
    overviewMonthlyReqType,
    /** Mobile Daily 等页面开启 Key Performance 行业基准入口（具体卡片由配置决定） */
    enableKeyPerformanceBenchmark = false,
  } = props;

  const { filterValue, timeType } = formValue;
  const { rangeTime } = filterValue ?? []
  const { formatMessage } = useIntl()

  const { EXPLAIN_MAP, DATE_DIMENSION, METRICS_NAME_MONTH, METRICS_NAME_WEEK, METRICS_NAME_DAY, METRICS_INFO } = useOverviewLocalesIntl()
  const GET_CONST_DATA = {
    [DATE_DIMENSION.DAY]: {
      METRICS_NAMES: METRICS_NAME_DAY,
      METRICS_NAME_SUFFIX: 'D',
      keyMetricsData: dateDimension === DATE_DIMENSION.DAY && useDailyKeyMetricsModel(parentProps, formValue, dispatch, filterLoading,dailyReqType),
      keyMetricsTrendData: dateDimension === DATE_DIMENSION.DAY && useDailyKeyMetricsTrendModel(parentProps, formValue, dispatch, filterLoading, dateDimension,dailyReqType),
    },
    [DATE_DIMENSION.WEEK]: {
      METRICS_NAMES: METRICS_NAME_WEEK,
      METRICS_NAME_SUFFIX: 'W',
      keyMetricsData: dateDimension === DATE_DIMENSION.WEEK && useWeeklyKeyMetricsModel(parentProps, formValue, dispatch, filterLoading),
      keyMetricsTrendData: dateDimension === DATE_DIMENSION.WEEK && useWeeklyKeyMetricsTrendModel(parentProps, formValue, dispatch, filterLoading, dateDimension),
    },
    [DATE_DIMENSION.MONTH]: {
      METRICS_NAMES: METRICS_NAME_MONTH,
      METRICS_NAME_SUFFIX: 'M',
      keyMetricsData: dateDimension === DATE_DIMENSION.MONTH && useMonthlyKeyMetricsModel(parentProps, formValue, dispatch, filterLoading, overviewMonthlyReqType),
      keyMetricsTrendData: dateDimension === DATE_DIMENSION.MONTH && useMonthlyKeyMetricsTrendModel(parentProps, formValue, dispatch, filterLoading, dateDimension, overviewMonthlyReqType),
    },
  }
  // 根据日期维度获取数据
  const { keyMetricsData, keyMetricsTrendData } = GET_CONST_DATA[dateDimension];

  const {
    componentData: keyMetricsList,
    error: keyMetricsError = false,
    loading: keyMetricsLoading = false,
  } = keyMetricsData || {}

  const {
    componentData: keyMetricsTrend,
    error: keyMetricsTrendError = false,
    loading: keyMetricsTrendLoading = false,
  } = keyMetricsTrendData || {}

  const { trendData, xAxisData } = useMemo(() => {
    const xAxisData: string[] = [];
    const activeUserCurrent: string[] = [];
    const activeUserLast: string[] = [];
    const revenueCurrent: string[] = [];
    const revenueLast: string[] = [];
    const newUserCurrent: string[] = [];
    const newUserLast: string[] = [];
    const dwm2Current: string[] = [];
    const dwm2Last: string[] = [];
    const arpuCurrent: string[] = [];
    const arpuLast: string[] = [];
    const payerRateCurrent: string[] = [];
    const payerRateLast: string[] = [];

    if (keyMetricsTrend && keyMetricsTrend?.Nodes) {
      for (const item of keyMetricsTrend?.Nodes) {
        xAxisData.push(item?.date);
        activeUserCurrent.push(dateDimension === 'DAY' ? item?.dau?.to_date : dateDimension === 'WEEK' ? item?.wau?.to_week : item?.mau?.this_year);
        activeUserLast.push(dateDimension === 'DAY' ? item?.dau?.last_week : dateDimension === 'WEEK' ? item?.wau?.last_month : item?.mau?.last_year);
        revenueCurrent.push(dateDimension === 'DAY' ? item?.revenue?.to_date : dateDimension === 'WEEK' ? item?.revenue?.to_week : item?.revenue?.this_year);
        revenueLast.push(dateDimension === 'DAY' ? item?.revenue?.last_week : dateDimension === 'WEEK' ? item?.revenue?.last_month : item?.revenue?.last_year);
        newUserCurrent.push(dateDimension === 'DAY' ? item?.new?.to_date : dateDimension === 'WEEK' ? item?.new?.to_week : item?.new?.this_year);
        newUserLast.push(dateDimension === 'DAY' ? item?.new?.last_week : dateDimension === 'WEEK' ? item?.new?.last_month : item?.new?.last_year);
        dwm2Current.push(dateDimension === 'DAY' ? item?.d2?.to_date : dateDimension === 'WEEK' ? item?.w2?.to_week : item?.m2?.this_year);
        dwm2Last.push(dateDimension === 'DAY' ? item?.d2?.last_week : dateDimension === 'WEEK' ? item?.w2?.last_month : item?.m2?.last_year);
        arpuCurrent.push(dateDimension === 'DAY' ? item?.arpu?.to_date : dateDimension === 'WEEK' ? item?.arpu?.to_week : item?.arpu?.this_year);
        arpuLast.push(dateDimension === 'DAY' ? item?.arpu?.last_week : dateDimension === 'WEEK' ? item?.arpu?.last_month : item?.arpu?.last_year);
        payerRateCurrent.push(dateDimension === 'DAY' ? item?.payer_ratio?.to_date : dateDimension === 'WEEK' ? item?.payer_ratio?.to_week : item?.payer_ratio?.this_year);
        payerRateLast.push(dateDimension === 'DAY' ? item?.payer_ratio?.last_week : dateDimension === 'WEEK' ? item?.payer_ratio?.last_month : item?.payer_ratio?.last_year);
      }
    }

    return {
      xAxisData,
      trendData: [
        [activeUserCurrent, activeUserLast],
        [revenueCurrent, revenueLast],
        [newUserCurrent, newUserLast],
        [dwm2Current, dwm2Last],
        [arpuCurrent, arpuLast],
        [payerRateCurrent, payerRateLast],
      ],
    }
  }, [keyMetricsTrend])

  const userQuota = [
    formatMessage({ id: 'overview_monthly_DAU' }),
    formatMessage({ id: 'overview_monthly_New' }),
    formatMessage({ id: 'overview_monthly_Avg_DAU' }),
    formatMessage({ id: 'overview_monthly_WAU' }),
    formatMessage({ id: 'overview_monthly_Avg_WAU' }),
    formatMessage({ id: 'overview_monthly_MAU' }),
    formatMessage({ id: 'overview_monthly_Avg_MAU' })
  ]

  const isChange = isGreater(dateDimension, rangeTime);
  // 卡片的指标解释应该从后端获取啊!!!
  const metricsExplainMap = {
    Day: {
      'DAU': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_au' }),
      'New': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_new' }),
      'Revenue': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_revenue' }),
      'ARPU': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_arpu' }),
      'Payer%': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_payer' }),
      'D2(T-1)': formatMessage({ id: 'overview_daily_key_performance_metrics_explain_d2' })
    },
    Week: {
      'WAU': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_au' }),
      'New': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_new' }),
      'Revenue': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_revenue' }),
      'ARPU': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_arpu' }),
      'Payer%': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_payer' }),
      'W2(T-1)': formatMessage({ id: 'overview_weekly_key_performance_metrics_explain_w2' })
    },
    Month: {
      'MAU': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_au' }),
      'New': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_new' }),
      'Revenue': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_revenue' }),
      'ARPU': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_arpu' }),
      'Payer%': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_payer' }),
      'M2(T-1)': formatMessage({ id: 'overview_monthly_key_performance_metrics_explain_m2' })
    },
  }

  // 根据不同的数据类型返回不同的字符串
  const getFormateStr = (type, value, name, metrics_name) => {
    // 这三个指标数值超过百万显示单位M|B
    const isMillionMetrics = new RegExp('^(DAU|Revenue|New)$', 'g').test(metrics_name) && value >= 1000000
    switch (type) {
      case 'int':
        if (isMillionMetrics) {
          return unitConversionMillion(value)
        }
        if (metrics_name === 'ARPU') return value < 1 && value > 0 ? formatSmallValue(value,'decimals',2) : formatNum(value, false, userQuota.includes(name))
        return formatNum(value, false, userQuota.includes(name));
      case 'percent':
        return formatPercentNum(parseFloat(value), 2);
    }
  }

  // 处理返回的卡片指标数据格式
  const metricsCardList = ((data = keyMetricsList?.card_list) => {
    const explainMap = EXPLAIN_MAP;
    const getExplain = (data) => {
      const newData = { ...(data || {}) };
      const keys = Object.keys(newData);
      for (const i in keys) {
        const key = keys[i];
        if (typeof newData[key] === 'object' && explainMap[key]) {
          newData[key].explain = explainMap[key];
        }
      }
      return newData;
    }

    const getJumpUrl = (name) => {
      const activeArr = ['DAU', 'MAU', 'WAU'];
      const revenueArr = ['Revenue', 'ARPU', 'Payer%'];
      const retentionArr = ['D2(T-1)', 'W2(T-1)', 'M2(T-1)'];
      let jumpUrl = '';
      if (activeArr.includes(name)) {
        jumpUrl = '/engagement/active';
      } else if (revenueArr.includes(name)) {
        jumpUrl = '/monetization/revenue';
      } else if (name === 'New') {
        jumpUrl = '/engagement/register';
      } else if (retentionArr.includes(name)) {
        jumpUrl = '/retention/index';
      } else {
        // 兜底跳转daily
        jumpUrl = '/overview/daily';
      }
      return `/${gameCode}${jumpUrl}`
    }

    return data?.map((item, index): MetricsItemInfo => {
      const { metrics_name, value, ...other } = item;
      const jumpUrl = getJumpUrl(metrics_name);
      const { title = "", type, end = "", avgTitle = "" } = METRICS_INFO[metrics_name];
      let name = `${title}${end}`
      if (isChange) {
        name = `${avgTitle}${end}`;
      }
      return {
        name,
        explain: metricsExplainMap?.[timeType]?.[metrics_name],
        metrics_name,
        value: getFormateStr(type, value, name, metrics_name),
        jumpUrl,
        ...getExplain(other),
        xAxisData,
        trendData: trendData[index]
      }
    })
  })()

  const componentItem = (
    <Row className="margin-t-10">
      {
        metricsCardList?.map((d, i) => {
          return (
            <Col key={d?.metrics_name} span={8} >
              <CardContext
                key={i}
                index={i}
                isChange={isChange}
                dataSource={d}
                formValue={formValue}
                userQuota={userQuota}
                dateDimension={dateDimension}
                enableKeyPerformanceBenchmark={enableKeyPerformanceBenchmark}
                gameCode={gameCode}
              />
            </Col>
          )
        })
      }
    </Row>
  )

  return (
    <Abnormal
      isLoading={keyMetricsLoading || keyMetricsTrendLoading}
      isError={keyMetricsError && keyMetricsTrendError}
      component={componentItem}
      style={{ height: isChange ? 312 : 314, background: '#fff', marginTop: 20 }}
    />
  );
};
